import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject } from 'rxjs';
import _ from 'lodash';

import { FuneralCost, FuneralCostItem } from '../../models/funeral-cost';
import { ModalService } from '../../services/modal.service';

/**
 * Dedicated editor for a single package — opened from
 * AccountFuneralCostListComponent. Multi-field form for the package envelope
 * plus an items sub-table of plain-text inclusions.
 *
 * Items are free text (label + optional qty/price/note). No catalogue
 * lookup, no computed price math; `package.price` is the brochure-site
 * headline. See `Conductor/PACKAGES.md` for why we abandoned the earlier
 * catalogue-linked model.
 *
 * Save shape: the mutated FuneralCost object is returned via dialogRef.close
 * to the list component, which writes it back into account.data.funeralCosts
 * and triggers the standard accountService.patch flow.
 */
@Component({
  selector: 'app-account-funeral-cost',
  templateUrl: './account-funeral-cost.component.html',
  styleUrls: ['./account-funeral-cost.component.scss'],
})
export class AccountFuneralCostComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  /** The package we're editing — a deep clone so cancel reverts cleanly. */
  pkg!: FuneralCost;
  isNew = false;
  /** True while the save request is in flight — Save spins, Cancel locks. */
  saving = false;

  /** Reactive form mirroring the package envelope. Items live separately. */
  form!: FormGroup;

  /** Items render as their own list. */
  items: FuneralCostItem[] = [];

  /** Region options for the pricing scope — Global + the content regions. */
  regionOptions: { label: string; value: string }[] = [{ label: 'Global (mainline site)', value: 'global' }];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService
  ) {}

  ngOnInit(): void {
    this.pkg = this.config.data.pkg;
    this.isNew = !!this.config.data.isNew;

    // Region options passed in by the list component (the content regions).
    const regions: { name?: string; slug?: string }[] = this.config.data.regions ?? [];
    this.regionOptions = [
      { label: 'Global (mainline site)', value: 'global' },
      ...regions
        .filter((r) => r && r.slug)
        .map((r) => ({ label: r.name || (r.slug as string), value: r.slug as string })),
    ];

    this.form = this.fb.group({
      name: new FormControl(this.pkg.name ?? '', [Validators.required]),
      slug: new FormControl(this.pkg.slug ?? '', [
        Validators.required,
        Validators.pattern(/^[a-z0-9-]+$/),
      ]),
      tagline: new FormControl(this.pkg.tagline ?? ''),
      description: new FormControl(this.pkg.description ?? ''),
      region: new FormControl(this.pkg.region ?? 'global'),
      isActive: new FormControl(this.pkg.isActive !== false),
      isFeatured: new FormControl(!!this.pkg.isFeatured),
      price: new FormControl(this.pkg.price ?? null),
      serviceFeePercent: new FormControl(this.pkg.serviceFeePercent ?? 40),
    });

    this.items = this.itemsAsList(this.pkg.items);
    // Normalise the fee flag to a real boolean so the switch binds correctly
    // (undefined === included, the default).
    this.items.forEach((i) => (i.includeInServiceFee = i.includeInServiceFee !== false));
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onAddItem(): void {
    const id = `id-item-${this.shortId()}`;
    const nextIndex = this.items.reduce(
      (max, i) => Math.max(max, i._meta.index ?? 0),
      -1
    ) + 1;
    this.items.push({
      _meta: { id, index: nextIndex },
      label: '',
      quantity: 1,
      includeInServiceFee: true, // default: subject to the service fee
    });
  }

  // ---- Live price breakdown -------------------------------------------------
  // Helpers the template binds to. An item counts toward the fee unless it's
  // been explicitly flipped off (includeInServiceFee === false); undefined ===
  // included, so legacy items and the default keep the current behaviour.

  /** Live (non-deleted) items. */
  private liveItems(): FuneralCostItem[] {
    return this.items.filter((i) => !i._meta.deleted);
  }

  /** Items excluded from the service fee, in display order (for footnotes). */
  excludedItems(): FuneralCostItem[] {
    return this.liveItems().filter((i) => i.includeInServiceFee === false);
  }

  /** The service-fee rate the form currently holds (default 40). */
  get feePercent(): number {
    const v = Number(this.form?.get('serviceFeePercent')?.value);
    return isFinite(v) && v >= 0 ? v : 40;
  }

  /** Sum of fee-bearing item prices (the base the service fee applies to). */
  get feeBearingSubtotal(): number {
    return this.liveItems()
      .filter((i) => i.includeInServiceFee !== false)
      .reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  }

  /** The service fee itself. */
  get serviceFeeAmount(): number {
    return this.round2(this.feeBearingSubtotal * (this.feePercent / 100));
  }

  /** Sum of pass-through (excluded) item prices — added at cost. */
  get passThroughTotal(): number {
    return this.excludedItems().reduce((sum, i) => sum + (Number(i.price) || 0), 0);
  }

  /** Computed total = fee-bearing sub-total + service fee + pass-through. */
  get computedTotal(): number {
    return this.round2(this.feeBearingSubtotal + this.serviceFeeAmount + this.passThroughTotal);
  }

  /** True when any live item carries a price (so the breakdown is meaningful). */
  get hasPricedItems(): boolean {
    return this.liveItems().some((i) => Number(i.price) > 0);
  }

  /** Footnote markers, assigned in order to excluded items. */
  private readonly markers = ['†', '‡', '§', '¶', '#', '*', '¤', '‖'];

  /** Marker for an excluded item (empty for included items). */
  markerFor(item: FuneralCostItem): string {
    if (item.includeInServiceFee !== false) return '';
    const idx = this.excludedItems().indexOf(item);
    return idx >= 0 ? this.markers[idx % this.markers.length] : '';
  }

  /** Fill the price field with the computed total. */
  useComputedTotal(): void {
    this.form.get('price')?.setValue(this.computedTotal);
  }

  private round2(n: number): number {
    return Math.round(n * 100) / 100;
  }

  onRemoveItem(item: FuneralCostItem): void {
    item._meta.deleted = true;
  }

  onRestoreItem(item: FuneralCostItem): void {
    item._meta.deleted = false;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    Object.assign(this.pkg, this.form.value);
    // Drop empty-label items on save — they're noise, and validation only
    // requires a non-empty label when an item exists at all (per PACKAGES.md §4).
    const kept = this.items.filter(
      (i) => i._meta.deleted || (typeof i.label === 'string' && i.label.trim().length > 0)
    );
    this.pkg.items = this.itemsAsMap(kept);

    // Save-then-close: the dialog stays open (Save spinning) until the server
    // confirms, so Publish cannot race an in-flight write. On rejection it
    // stays open with everything typed — fix and retry, nothing lost.
    const save = this.config.data.save as ((p: FuneralCost) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, pkg: this.pkg });
      return;
    }
    this.saving = true;
    save(this.pkg).subscribe({
      next: () => this.ref.close({ saved: true, pkg: this.pkg, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }

  /** Object-collection → array, sorted by _meta.index, deleted items at end. */
  private itemsAsList(raw: { [id: string]: FuneralCostItem } | FuneralCostItem[] | undefined): FuneralCostItem[] {
    if (!raw) return [];
    const list = Array.isArray(raw) ? raw.filter(Boolean) : Object.values(raw).filter(Boolean);
    return _.sortBy(
      list,
      [(i) => (i._meta?.deleted ? 1 : 0), (i) => i._meta?.index ?? Number.MAX_SAFE_INTEGER]
    );
  }

  /** Array → object-collection keyed by _meta.id. */
  private itemsAsMap(list: FuneralCostItem[]): { [id: string]: FuneralCostItem } {
    const out: { [id: string]: FuneralCostItem } = {};
    list.forEach((item, index) => {
      // Re-number indices so they're contiguous regardless of edit history.
      item._meta.index = index;
      out[item._meta.id] = item;
    });
    return out;
  }

  private shortId(): string {
    return Math.random().toString(36).slice(2, 10);
  }
}
